import {
  useState,
  ReactElement,
  Dispatch,
  SetStateAction,
  useEffect,
  MutableRefObject,
  useRef,
  useContext,
} from "react";
import { ToolbarProps, ToolbarSlot } from "@react-pdf-viewer/default-layout";
import { HighlightStateContext } from "../../util";
import { InlineButton, TextField } from "../../components";
import { makeStyles, Box, Fade, CircularProgress } from "@material-ui/core";
import { colorsLight } from "../../themes";
import clsx from "clsx";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import ZoomOutIcon from "@material-ui/icons/ZoomOut";
import ZoomInIcon from "@material-ui/icons/ZoomIn";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import Rotate90DegreesCcwIcon from "@material-ui/icons/Rotate90DegreesCcw";
import { KeyboardArrowUp, KeyboardArrowDown, ArrowBack, FormatListBulleted } from "@material-ui/icons";
import { SpecialZoomLevel, RotateDirection } from "@react-pdf-viewer/core";
import { RenderZoomInProps, RenderZoomProps } from "@react-pdf-viewer/zoom";
import { usePdfToolbarControlHandlers } from "./hooks/usePdfToolbarControlHandlers";

export interface FaxOutcomeToolbarControlProps {
  Toolbar: (props: ToolbarProps) => ReactElement;
  currentRotate?: MutableRefObject<number>;
  hideSidePanelButton?: boolean;
  openSidePanel?: boolean;
  setOpenSidePanel?: Dispatch<SetStateAction<boolean>>;
  textInputRef?: MutableRefObject<HTMLInputElement | undefined>;
  loadingRemainingAttachments?: boolean;
}

export default function FaxOutcomeToolbarControl({
  currentRotate,
  openSidePanel,
  setOpenSidePanel,
  textInputRef,
  loadingRemainingAttachments = false,
  hideSidePanelButton = false,
  Toolbar,
}: FaxOutcomeToolbarControlProps) {
  const toolbarClasses = useToolbarStyles();
  const searchClasses = useStylesSearchBar();

  const rotateRef = useRef<HTMLButtonElement>(null);
  const [rotateLeft, setRotateLeft] = useState<number>(Math.abs(currentRotate?.current || 0) / 90);

  const [showSearchResults, setShowSearchResults] = useState<boolean>(false);
  const [isSearchTextChanged, setIsSearchTextChanged] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");

  // Required to fix @react-pdf-viewer "Black sections when new documents are loaded" bug.
  // Internal library bug, already a Github issue, as a workaround ZoomIn/ZoomOut seems to fix it.
  const zoomInProps = useRef<RenderZoomInProps | null>(null);
  const zoomProps = useRef<RenderZoomProps | null>(null);

  const hasRendered = useRef<boolean>(false);

  useEffect(() => {
    setTimeout(() => {
      if (zoomInProps.current) {
        zoomInProps.current.onClick();
      }

      if (zoomProps.current) {
        zoomProps.current.onZoom(SpecialZoomLevel.PageWidth);
      }
    }, 1);
  }, []);

  useEffect(() => {
    if (rotateRef && currentRotate && rotateLeft > 0) {
      rotateRef?.current?.click();
      setRotateLeft(rotateLeft - 1);
    }
  }, [currentRotate, rotateLeft]);

  const { linking } = useContext(HighlightStateContext);

  useEffect(() => {
    if (hasRendered.current) {
      return;
    }
    hasRendered.current = true;
  }, [textInputRef]);

  const {
    calculateCurrentMatchNumber,
    calculateTotalMatchesNumber,
    onClearSearchInputValue,
    onSearchInputChangeValue,
    onSearchInputKeyDown,
    doJumpToNextMatch,
    doJumpToPreviousMatch,
    doSearch,
  } = usePdfToolbarControlHandlers({
    isSearchTextChanged,
    setIsSearchTextChanged,
    setSearchText,
    setShowSearchResults,
  });

  useEffect(() => {
    if (rotateRef && currentRotate && rotateLeft > 0) {
      rotateRef?.current?.click();
      setRotateLeft(rotateLeft - 1);
    }
  }, [currentRotate, rotateLeft]);

  return (
    <Toolbar>
      {(slots: ToolbarSlot) => {
        const { CurrentPageLabel, NumberOfPages, ZoomIn, ZoomOut, Zoom, Rotate, Search } = slots;
        return (
          <div className={toolbarClasses.container}>
            {!hideSidePanelButton && (
              <Box className={clsx(toolbarClasses.toolbarControls, toolbarClasses.toolbarLeftControls)}>
                <div className={toolbarClasses.toolbarItemWrapper}>
                  <ToggleListButton openSidePanel={openSidePanel} setOpenSidePanel={setOpenSidePanel} />
                </div>
              </Box>
            )}

            <Box className={clsx(toolbarClasses.toolbarControls, toolbarClasses.toolbarCenterControls)}>
              <div className={toolbarClasses.toolbarItemWrapper}>
                <div className={toolbarClasses.pageCounter}>
                  Page <CurrentPageLabel /> of <NumberOfPages />
                </div>
              </div>
              <div className={toolbarClasses.toolbarItemWrapper}>
                <Rotate direction={RotateDirection.Backward}>
                  {(props) => (
                    <InlineButton
                      className={toolbarClasses.toolbarInlineButton}
                      ref={rotateRef}
                      onClick={() => {
                        props.onClick();
                      }}
                      title="Rotate PDF 90 degrees"
                      aria-label="Rotate PDF 90 degrees"
                    >
                      <Rotate90DegreesCcwIcon />
                    </InlineButton>
                  )}
                </Rotate>
              </div>
              <div className={toolbarClasses.toolbarItemWrapper}>
                <ZoomOut>
                  {(props) => (
                    <InlineButton
                      className={toolbarClasses.toolbarInlineButton}
                      onClick={() => {
                        props.onClick();
                      }}
                      title="Zoom out"
                      aria-label="Zoom out"
                    >
                      <ZoomOutIcon />
                    </InlineButton>
                  )}
                </ZoomOut>
                <ZoomIn>
                  {(props) => {
                    zoomInProps.current = props;
                    return (
                      <InlineButton
                        className={toolbarClasses.toolbarInlineButton}
                        onClick={() => {
                          props.onClick();
                        }}
                        title="Zoom in"
                        aria-label="Zoom in"
                      >
                        <ZoomInIcon />
                      </InlineButton>
                    );
                  }}
                </ZoomIn>
                <Zoom>
                  {(props) => {
                    zoomProps.current = props;
                    return (
                      <InlineButton
                        className={toolbarClasses.toolbarInlineButton}
                        onClick={() => {
                          props.onZoom(SpecialZoomLevel.PageWidth);
                        }}
                        title="Zoom to fit content"
                        aria-label="Zoom to fit content"
                      >
                        <FullscreenIcon />
                      </InlineButton>
                    );
                  }}
                </Zoom>
              </div>

              <Box className={toolbarClasses.toolbarControls}>
                <Search>
                  {(props) => {
                    return (
                      <>
                        <div className={toolbarClasses.toolbarItemWrapper} style={{ flexShrink: 4 }}>
                          <TextField
                            inputRef={textInputRef}
                            value={searchText}
                            onChangeValue={(text) => onSearchInputChangeValue(text, props)}
                            placeholder={`Search text in attachment`}
                            title={`Search text in attachment`}
                            aria-label={`Search text in attachment`}
                            onKeyDown={(e) => onSearchInputKeyDown(e, props)}
                            classes={{ root: searchClasses.root }}
                            InputProps={{
                              endAdornment: (
                                <>
                                  {searchText && loadingRemainingAttachments ? (
                                    <CircularProgress size={20} className={searchClasses.progress} />
                                  ) : showSearchResults && searchText ? (
                                    <>
                                      <div className={searchClasses.searchMatches}>
                                        {calculateCurrentMatchNumber(props)} of {calculateTotalMatchesNumber(props)}
                                      </div>
                                      <InlineButton
                                        className={searchClasses.buttons}
                                        style={{ paddingTop: 12 }}
                                        onClick={() => onClearSearchInputValue(props)}
                                        title="Clear search box"
                                      >
                                        <ClearIcon className={searchClasses.icons} />
                                      </InlineButton>
                                    </>
                                  ) : (
                                    <InlineButton
                                      className={searchClasses.buttons}
                                      style={{ paddingTop: 12 }}
                                      disabled={!props.keyword}
                                      onClick={() => doSearch({ props, autoFocusInput: false })}
                                      title="Search"
                                    >
                                      <SearchIcon className={searchClasses.icons} />
                                    </InlineButton>
                                  )}
                                </>
                              ),
                            }}
                          />
                        </div>
                        <div className={toolbarClasses.toolbarItemWrapper}>
                          <InlineButton
                            className={toolbarClasses.toolbarInlineButton}
                            disabled={calculateTotalMatchesNumber(props) === 0 || !searchText || !!linking}
                            onClick={() => doJumpToPreviousMatch({ props, autoFocusInput: false })}
                            title="Jump to previous match"
                            aria-label="Jump to previous match"
                          >
                            <KeyboardArrowUp className={searchClasses.icons} />
                          </InlineButton>
                          <InlineButton
                            className={toolbarClasses.toolbarInlineButton}
                            disabled={calculateTotalMatchesNumber(props) === 0 || !searchText || !!linking}
                            onClick={() => doJumpToNextMatch({ props, autoFocusInput: false })}
                            title="Jump to next match"
                            aria-label="Jump to next match"
                          >
                            <KeyboardArrowDown className={searchClasses.icons} />
                          </InlineButton>
                        </div>
                      </>
                    );
                  }}
                </Search>
              </Box>
            </Box>
          </div>
        );
      }}
    </Toolbar>
  );
}

interface ToggleListButtonProps {
  openSidePanel?: boolean;
  setOpenSidePanel?: Dispatch<SetStateAction<boolean>>;
}

export const ToggleListButton = ({ setOpenSidePanel, openSidePanel }: ToggleListButtonProps) => {
  const labelCaption = `${openSidePanel ? "Hide" : "Show"} ${"attachment list"}`;

  const toolbarClasses = useToolbarStyles();

  return (
    <InlineButton
      className={toolbarClasses.toolbarInlineButton}
      style={{ padding: 0 }}
      onClick={() => setOpenSidePanel?.((prevOpenSidePanel) => !prevOpenSidePanel)}
      title={labelCaption}
      aria-label={labelCaption}
    >
      <Box display="flex" alignItems="center">
        <Box>
          <Fade in={openSidePanel} mountOnEnter unmountOnExit>
            <Box display="flex" alignItems="center" justifyContent="center">
              <ArrowBack />
            </Box>
          </Fade>
          <Fade in={!openSidePanel} mountOnEnter unmountOnExit>
            <Box display="flex" alignItems="center" justifyContent="center">
              <FormatListBulleted />
            </Box>
          </Fade>
        </Box>
      </Box>
    </InlineButton>
  );
};

export const useToolbarStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    paddingInline: "16px",
    backgroundColor: colorsLight.primary.contrastText,
  },
  toolbarControls: {
    alignItems: "center",
    display: "flex",
  },
  toolbarCenterControls: {
    flex: 1,
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    justifyContent: "center",
    gap: theme.spacing(2),
  },
  toolbarLeftControls: {
    marginLeft: theme.spacing(2),
    justifyContent: "flex-start",
  },
  toolbarRightControls: {
    flexGrow: 2,
    justifyContent: "center",
  },
  toolbarItemWrapper: {
    display: "flex",
    alignItems: "center",
  },
  toolbarInlineButton: {
    color: theme.palette.text.tertiary,
    padding: theme.spacing(0.75, 0.75, 0),
    minWidth: 20,
    "&:focus": {
      color: theme.palette.text.tertiary,
    },
    "&:hover,&:active": {
      color: theme.palette.primary.main,
    },
  },
  pageCounter: {
    color: theme.palette.text.tertiary,
    fontFamily: "Gilroy-Medium",
    fontSize: "13px",
    fontWeight: 400,
  },
}));

const useStylesSearchBar = makeStyles((theme) => ({
  root: {
    flex: 1,
    display: "flex",
    minWidth: "20vw",
    height: "24px",
    verticalAlign: "middle",
    [theme.breakpoints.down("lg")]: {
      width: "26vw",
    },
    "& .MuiFilledInput-root": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
      fontSize: 13,
    },
    "& .MuiFilledInput-input": {
      color: colorsLight.font.light,
    },
  },
  searchMatches: {
    maxWidth: "max-content",
    whiteSpace: "nowrap",
    textAlign: "right",
    marginRight: "10px",
    paddingLeft: "10px",
  },
  buttons: {
    minWidth: theme.spacing(1),
    width: theme.spacing(2),
    display: "flex",
    color: theme.palette.text.tertiary,
    "&:focus": {
      color: theme.palette.text.tertiary,
    },
    "&:hover,&:active": {
      color: colorsLight.primary.main,
    },
  },
  progress: { opacity: "0.6", display: "flex", alignItems: "center" },
  icons: { height: 20, width: 20 },
}));
